import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import FileUploadSection from '../common/UI/FileUploadSection';
import 'react-color-palette/css';
import notify from '@/services/notify';
import './style.scss';
import {
  ASPECT_RATIO_OPTIONS,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_IMAGE_GEN_RESOLUTION,
  IMAGE_GEN_RESOLUTIONS,
} from '@/config/settings';
import { ColorPicker, IColor, useColor, ColorService } from 'react-color-palette';
import FileUploadBadge from '../common/UI/FileUploadBadge';
import { deleteImage, uploadImage } from '@/services/aiApps';
import ColorItem from '../common/UI/ColorItem';
import useElysiaStore from '@/app/elysia-store';
import CloseIcon from '@/assets/icon-close.svg?react';
const ConfigureImageModal = ({ initiated, setInitiated }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isColorPaletteOpen, setIsColorPaletteOpen] = useState(false);
  const [color, setColor] = useColor('#561ecb');
  const [colors, setColors] = useState<IColor[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('auto');
  const [resolution, setResolution] = useState(DEFAULT_IMAGE_GEN_RESOLUTION);

  const imageGenPayload = useElysiaStore((state) => state.imageGenPayload);
  const uploadedImageId = useElysiaStore((state) => state.uploadedImageId);
  const activeChatHistoryRecord = useElysiaStore((state) => state.activeChatHistoryRecord);
  const setIstoolsButtonSelected = useElysiaStore.getState().setIstoolsButtonSelected;
  const setImageGenPayload = useElysiaStore.getState().setImageGenPayload;
  const setUploadedImageId = useElysiaStore.getState().setUploadedImageId;

  const [fileName, setFileName] = useState('');

  const [isWideScreen, setIsWideScreen] = useState(false);
  const MAX_COLORS_COUNT = 5;
  const MAX_SIZE = 52428800; // 50 MB

  useEffect(() => {
    if (activeChatHistoryRecord?.history) {
      processColors(imageGenPayload.color_palette);
      setAspectRatio(imageGenPayload.aspect_ratio);
      setUploadedImageId(imageGenPayload.starter_image_id);
      setResolution(imageGenPayload?.image_quality);
      if (imageGenPayload.starter_image_id?.length > 0) {
        setFileName('image.png');
      } else {
        setFileName('');
      }
    }
    //eslint-disable-next-line
  }, [imageGenPayload, activeChatHistoryRecord]);

  useEffect(() => {
    if (initiated) {
      setIsModalOpen(true);
    }
  }, [initiated]);

  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (uploadedImageId.length > 0 || aspectRatio !== DEFAULT_ASPECT_RATIO || colors.length > 0) {
      setIstoolsButtonSelected(true);
    } else {
      setIstoolsButtonSelected(false);
    }
    //eslint-disable-next-line
  }, [uploadedImageId, aspectRatio, colors]);

  const handleClose = () => {
    setIsModalOpen(false);
    setInitiated(false);
  };

  const handleFiles = (e) => {
    handleImageUpload(e[0]);
  };

  const processColors = (resColors) => {
    const updatedColors = resColors.map((color) => {
      const rgb = ColorService.hex2rgb(color);
      const hsv = ColorService.rgb2hsv(rgb);
      const obj = {
        hex: color,
        hsv: hsv,
        rgb: rgb,
      };
      return obj;
    });
    setColors(updatedColors);
  };

  const addColor = () => {
    if (editingIndex !== null) {
      const updatedColors = [...colors];
      updatedColors[editingIndex] = color;
      setColors(updatedColors);
      setEditingIndex(null);
    } else {
      if (colors.length >= MAX_COLORS_COUNT) {
        return;
      }

      const alreadyExists = colors.some((c) => c.hex === color.hex);
      if (alreadyExists) {
        notify({
          message: 'Colour already added.',
          type: 'error',
        });
        return;
      }

      setColors([...colors, color]);
    }

    setIsColorPaletteOpen(false);
  };

  const deleteColor = (hexToDelete) => {
    setColors(colors.filter((hex) => hex !== hexToDelete));
  };

  const handleImageUpload = async (image: File) => {
    if (!image) return;
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const isValidSize = image.size <= MAX_SIZE;

    if (!allowedTypes.includes(image.type) || !isValidSize) {
      notify({
        message: 'The supported image types are *.png, *.jpg. Maximum 1 image, 50mb.',
        type: 'error',
      });
      return;
    } else {
      try {
        setLoadingFile(true);
        setFileName(image?.name || 'image');

        const response = await uploadImage(image);
        if (response) {
          setUploadedImageId(response?.uuid);
          setLoadingFile(false);
        }
      } catch (error) {
        console.error('Error uploading icon:', error);
        notify({
          message: 'failed to upload Image.',
          type: 'error',
        });
      }
    }
  };

  const handleFileRemove = () => {
    deleteImage(uploadedImageId);
    setUploadedImageId('');
    setFileName('');
  };

  const generatePayload = () => {
    const payload = {
      aspect_ratio: aspectRatio,
      color_palette: colors.map((color) => color.hex),
      starter_image_id: uploadedImageId,
      image_quality: resolution,
    };
    setImageGenPayload(payload);
  };

  const handleProceed = () => {
    generatePayload();
    handleClose();
  };

  return (
    <Modal
      show={isModalOpen}
      onHide={handleClose}
      className="image-generator-modal"
      dialogClassName="custom-modal"
      centered={isWideScreen}
      backdrop="static"
    >
      <Modal.Header>
        <div className="header-content">
          <div>
            <Modal.Title>Configure Image</Modal.Title>
          </div>
          <Button type="button" className="btn-close" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </Button>
        </div>
        <div className="instruction-row">
          <span>
            Configure your image settings in this panel, or use the prompt box to generate images
            instantly.
          </span>
        </div>
        <div className="header-border-line" />
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-2">
          <h5>Starter Image</h5>
        </div>
        <p>Upload 1 image (limit 50MB) to guide Elysia's image generation.</p>

        {fileName.length > 0 ? (
          <div className="uploaded-file-wrapper">
            <FileUploadBadge
              uploadedFileTitle={fileName}
              onRemove={handleFileRemove}
              fileStatus={loadingFile}
            />
          </div>
        ) : (
          <div className="file-wrapper">
            <FileUploadSection
              accept={{
                'image/png': ['.png'],
                'image/jpeg': ['.jpeg', '.jpg'],
              }}
              maxFiles={1}
              multiple={false}
              maxSize={MAX_SIZE}
              onFilesAccepted={handleFiles}
              errorMsg="The supported image types are *.png, *.jpg. Maximum 1 image, 50mb."
              fileFormatExtensionsText="(*.png, *.jpg)"
            />
          </div>
        )}

        <div className="mt-4">
          <div className="mt-4">
            <h5>Size/Aspect Ratio</h5>
            <Form.Group>
              <Form.Label>
                <strong>Select Size</strong>
              </Form.Label>
              <Form.Select
                onChange={(e) => setAspectRatio(e.target.value)}
                className="aspect-ratio"
                defaultValue={aspectRatio}
                aria-label="Select aspect ratio"
              >
                {ASPECT_RATIO_OPTIONS.map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <div className="mt-4">
            <h5>Resolution</h5>
            <Form.Group>
              <Form.Label>
                <strong>Select Resolution</strong>
              </Form.Label>
              <Form.Select
                onChange={(e) => setResolution(e.target.value)}
                className="aspect-ratio"
                defaultValue={resolution}
                aria-label="Select Resolution"
              >
                {IMAGE_GEN_RESOLUTIONS.map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="mt-4">
            <div className="d-flex gap-2">
              <h5>Colour Palette</h5>
            </div>

            <p className="m-0">Add colour(s) to be used in your image (max 5)</p>
            <div className=" row g-2 mt-1 gap-3">
              {colors.map((hex, index) => (
                <ColorItem
                  key={index}
                  hex={hex}
                  index={index}
                  onEdit={(idx, color) => {
                    setColor(color);
                    setEditingIndex(idx);
                    setIsColorPaletteOpen(true);
                  }}
                  onDelete={deleteColor}
                />
              ))}
            </div>
            <div>
              {isColorPaletteOpen && (
                <div className="custom-color-picker">
                  <div className="custom-color-picker__header">
                    <button
                      className="btn-close"
                      onClick={() => setIsColorPaletteOpen(false)}
                      aria-label="Close color picker"
                    ></button>
                  </div>

                  <div>
                    <ColorPicker color={color} onChange={setColor} hideInput={['rgb', 'hsv']} />
                  </div>

                  <Button className="update-btn" onClick={addColor}>
                    Add Colour
                  </Button>
                </div>
              )}
            </div>
            {!isColorPaletteOpen && (
              <>
                <Button
                  className="update-btn mt-3"
                  onClick={() => setIsColorPaletteOpen(true)}
                  disabled={colors.length >= MAX_COLORS_COUNT}
                >
                  Add Colour
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-100 d-flex justify-content-between">
          <Button className="update-btn" onClick={handleClose}>
            Cancel
          </Button>
          <Button className="update-btn proceed-btn" onClick={handleProceed} disabled={loadingFile}>
            Proceed
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfigureImageModal;
